import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Loader2, RefreshCw, ShoppingCart, Check, XCircle, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { ComplianceViolations } from './ComplianceViolations';

interface Listing {
  id: string;
  company_id: string;
  seller_id: string;
  share_class: string;
  shares: number;
  price_per_share: number;
  status: string;
  latest_offer_id: string | null;
  created_at: string;
}

export default function SecondaryMarketplace({ companyId }: { companyId: string }) {
  const { user } = useAuth();
  const [listings, setListings] = useState<Listing[]>([]);
  const [shares, setShares] = useState('');
  const [price, setPrice] = useState('');
  const [shareClass, setShareClass] = useState('Ordinary');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [offerDialogOpen, setOfferDialogOpen] = useState(false);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const [offerPrice, setOfferPrice] = useState('');
  const [complianceViolations, setComplianceViolations] = useState<any[]>([]);

  async function load() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('secondary_listings')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setListings(data || []);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load listings');
    } finally {
      setLoading(false);
    }
  }

  async function createListing() {
    if (!shares || !price) {
      toast.error('Enter shares and price');
      return;
    }

    setCreating(true);
    setComplianceViolations([]);
    try {
      const { data, error } = await supabase.functions.invoke('create-listing', {
        body: {
          companyId,
          shares: Number(shares),
          pricePerShare: Number(price),
          shareClass,
        },
      });

      if (error) {
        // Check if it's a compliance error
        if (error.message?.includes('Compliance check failed')) {
          const violations = (error as any).violations || [];
          setComplianceViolations(violations);
          toast.error('Compliance check failed');
          return;
        }
        throw error;
      }

      toast.success('Listing created successfully');
      setShares('');
      setPrice('');
      setComplianceViolations([]);
      load();
    } catch (error: any) {
      toast.error(error.message || 'Failed to create listing');
    } finally {
      setCreating(false);
    }
  }

  async function placeOffer(listing: Listing) {
    if (!offerPrice) {
      toast.error('Enter offer price');
      return;
    }

    setComplianceViolations([]);
    try {
      const { data, error } = await supabase.functions.invoke('place-offer', {
        body: {
          listingId: listing.id,
          offerPrice: Number(offerPrice),
        },
      });

      if (error) {
        // Check if it's a compliance error
        if (error.message?.includes('Compliance check failed')) {
          const violations = (error as any).violations || [];
          setComplianceViolations(violations);
          toast.error('Compliance check failed');
          return;
        }
        throw error;
      }

      toast.success('Offer placed successfully');
      setOfferDialogOpen(false);
      setOfferPrice('');
      setSelectedListing(null);
      setComplianceViolations([]);
      load();
    } catch (error: any) {
      toast.error(error.message || 'Failed to place offer');
    }
  }

  async function acceptOffer(offerId: string) {
    try {
      const { error } = await supabase.functions.invoke('accept-offer', {
        body: { offerId },
      });

      if (error) throw error;

      toast.success('Offer accepted and transfer initiated');
      load();
    } catch (error: any) {
      toast.error(error.message || 'Failed to accept offer');
    }
  }

  useEffect(() => {
    if (companyId) {
      load();
    }
  }, [companyId]);

  return (
    <Card className="border-border bg-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Secondary Marketplace
        </CardTitle>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Compliance Violations */}
        <ComplianceViolations violations={complianceViolations} />

        {/* Create Listing */}
        <div className="p-4 rounded-lg border border-border bg-background space-y-3">
          <h3 className="text-sm font-semibold">Create New Listing</h3>
          <div className="grid sm:grid-cols-4 gap-2">
            <Input
              placeholder="Shares"
              type="number"
              value={shares}
              onChange={(e) => setShares(e.target.value)}
              min="1"
            />
            <Input
              placeholder="Price per share"
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              min="0.01"
              step="0.01"
            />
            <Input
              placeholder="Share Class"
              value={shareClass}
              onChange={(e) => setShareClass(e.target.value)}
            />
            <Button onClick={createListing} disabled={creating || !shares || !price}>
              {creating ? (
                <Loader2 className="animate-spin h-4 w-4" />
              ) : (
                <>
                  <ShoppingCart className="h-4 w-4 mr-1" />
                  List Shares
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Listings Table */}
        {loading ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="animate-spin h-8 w-8 text-muted-foreground" />
          </div>
        ) : listings.length > 0 ? (
          <div className="rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Share Class</TableHead>
                  <TableHead className="text-right">Shares</TableHead>
                  <TableHead className="text-right">Price/Share</TableHead>
                  <TableHead className="text-right">Total Value</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listings.map((listing) => (
                  <TableRow key={listing.id}>
                    <TableCell className="font-medium">{listing.share_class}</TableCell>
                    <TableCell className="text-right">{listing.shares.toLocaleString()}</TableCell>
                    <TableCell className="text-right">
                      R{listing.price_per_share.toFixed(4)}
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      R{(listing.shares * listing.price_per_share).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          listing.status === 'OPEN'
                            ? 'default'
                            : listing.status === 'OFFERED'
                              ? 'secondary'
                              : listing.status === 'ACCEPTED'
                                ? 'outline'
                                : 'secondary'
                        }
                      >
                        {listing.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        {listing.status === 'OPEN' && listing.seller_id !== user?.id && (
                          <Dialog
                            open={offerDialogOpen && selectedListing?.id === listing.id}
                            onOpenChange={setOfferDialogOpen}
                          >
                            <DialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setSelectedListing(listing)}
                              >
                                Place Offer
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Place Offer</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4 pt-4">
                                <div>
                                  <p className="text-sm text-muted-foreground mb-2">
                                    Listing: {listing.shares} {listing.share_class} shares at R
                                    {listing.price_per_share.toFixed(4)} each
                                  </p>
                                  <Input
                                    placeholder="Your offer price per share"
                                    type="number"
                                    value={offerPrice}
                                    onChange={(e) => setOfferPrice(e.target.value)}
                                    min="0.01"
                                    step="0.01"
                                  />
                                </div>
                                <Button
                                  onClick={() => placeOffer(listing)}
                                  disabled={!offerPrice}
                                  className="w-full"
                                >
                                  Submit Offer
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        )}
                        {listing.status === 'OFFERED' &&
                          listing.seller_id === user?.id &&
                          listing.latest_offer_id && (
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => acceptOffer(listing.latest_offer_id!)}
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Accept Offer
                            </Button>
                          )}
                        {(listing.status === 'CLOSED' || listing.status === 'ACCEPTED') && (
                          <XCircle className="text-muted-foreground h-4 w-4" />
                        )}
                        {listing.seller_id === user?.id && listing.status === 'OPEN' && (
                          <Badge variant="secondary" className="text-xs">
                            Your Listing
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="p-8 text-center border border-dashed border-border rounded-lg">
            <ShoppingCart className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
            <p className="text-sm text-muted-foreground">No active listings.</p>
            <p className="text-xs text-muted-foreground mt-1">
              Create the first listing to start trading shares.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
